import {
	Alert,
	Badge,
	Button,
	Code,
	Divider,
	Drawer,
	Group,
	Skeleton,
	Stack,
	Text,
	Textarea,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { useApiQuery } from "@/hooks/useApiQuery";
import { i18nStore } from "@/store/i18n";
import { fetchServerLogDetail, updateServerLogStatus } from "./server-log.api";
import { formatDateTime, levelColor, statusColor } from "./server-log.logic";
import { useServerLogText } from "./use-server-log-text";

const NOTE_MAX_CHARS = 500;
const STACK_MAX_HEIGHT = 240;

interface Props {
	groupId: string | null;
	onClose: () => void;
}

/** Drawer detail grup: kejadian terbaru + stack, tombol tangani/abaikan/buka lagi. */
export function ServerLogDrawer({ groupId, onClose }: Props) {
	const t = useServerLogText();
	const { lang } = useSnapshot(i18nStore);
	const locale = lang === "en" ? "en-US" : "id-ID";
	const queryClient = useQueryClient();
	const [note, setNote] = useState("");
	const [feedback, setFeedback] = useState<{
		ok: boolean;
		text: string;
	} | null>(null);
	const [saving, setSaving] = useState(false);

	const { data, isLoading } = useApiQuery(
		["server-log", "detail", groupId],
		() => fetchServerLogDetail(groupId as string),
		{ enabled: Boolean(groupId) },
	);

	useEffect(() => {
		setNote(data?.note ?? "");
	}, [data?.note]);

	useEffect(() => {
		if (groupId) setFeedback(null);
	}, [groupId]);

	const apply = async (status: "open" | "resolved" | "ignored") => {
		if (!groupId) return;
		setSaving(true);
		try {
			await updateServerLogStatus(groupId, status, note);
			await queryClient.invalidateQueries({ queryKey: ["server-log"] });
			setFeedback({ ok: true, text: t.detail.saved });
		} catch {
			setFeedback({ ok: false, text: t.detail.saveFailed });
		} finally {
			setSaving(false);
		}
	};

	const latestStack = data?.entries.find((e) => e.stack)?.stack;

	return (
		<Drawer
			opened={Boolean(groupId)}
			onClose={onClose}
			position="right"
			size="xl"
			title={data ? `[${data.module}]` : t.title}
		>
			{isLoading && <Skeleton h={160} />}
			{data && (
				<Stack gap="md">
					<Group gap="xs">
						<Badge color={levelColor(data.level)} variant="light">
							{t.levels[data.level] ?? data.level}
						</Badge>
						<Badge color={statusColor(data.status)} variant="dot">
							{t.statuses[data.status] ?? data.status}
						</Badge>
					</Group>
					<Text fw={600}>{data.title}</Text>
					{data.reopenedAt && data.status === "regressed" && (
						<Alert color="orange" variant="light">
							{t.detail.regressed} ({formatDateTime(data.reopenedAt, locale)})
						</Alert>
					)}
					<Group gap="xl">
						<Meta
							label={t.detail.count}
							value={data.count.toLocaleString(locale)}
						/>
						<Meta
							label={t.detail.firstSeen}
							value={formatDateTime(data.firstSeenAt, locale)}
						/>
						<Meta
							label={t.detail.lastSeen}
							value={formatDateTime(data.lastSeenAt, locale)}
						/>
					</Group>
					<Meta label={t.detail.fingerprint} value={data.fingerprint} mono />

					<Textarea
						label={t.detail.note}
						placeholder={t.detail.notePlaceholder}
						value={note}
						maxLength={NOTE_MAX_CHARS}
						autosize
						minRows={2}
						onChange={(e) => setNote(e.currentTarget.value)}
					/>
					<Group>
						{data.status === "resolved" || data.status === "ignored" ? (
							<Button
								variant="light"
								loading={saving}
								onClick={() => apply("open")}
							>
								{t.detail.reopen}
							</Button>
						) : (
							<>
								<Button
									color="green"
									loading={saving}
									onClick={() => apply("resolved")}
								>
									{t.detail.handled}
								</Button>
								<Button
									color="gray"
									variant="light"
									loading={saving}
									onClick={() => apply("ignored")}
								>
									{t.detail.ignore}
								</Button>
							</>
						)}
					</Group>
					{feedback && (
						<Alert color={feedback.ok ? "green" : "red"} variant="light">
							{feedback.text}
						</Alert>
					)}

					<Divider label={t.detail.stack} />
					{latestStack ? (
						<Code
							block
							style={{ maxHeight: STACK_MAX_HEIGHT, overflow: "auto" }}
						>
							{latestStack}
						</Code>
					) : (
						<Text size="sm" c="dimmed">
							-
						</Text>
					)}

					<Divider label={t.detail.entries} />
					{data.entries.length === 0 && (
						<Text size="sm" c="dimmed">
							{t.detail.noEntries}
						</Text>
					)}
					{data.entries.map((e) => (
						<Stack key={e.id} gap={2}>
							<Text size="xs" c="dimmed">
								{formatDateTime(e.at, locale)}
								{e.method && e.route ? ` · ${e.method} ${e.route}` : ""}
								{e.statusCode ? ` · ${e.statusCode}` : ""}
								{e.userId ? ` · ${t.detail.user}: ${e.userId}` : ""}
							</Text>
							<Text size="sm" style={{ wordBreak: "break-word" }}>
								{e.message}
							</Text>
						</Stack>
					))}
				</Stack>
			)}
		</Drawer>
	);
}

function Meta({
	label,
	value,
	mono,
}: {
	label: string;
	value: string;
	mono?: boolean;
}) {
	return (
		<Stack gap={0}>
			<Text size="xs" c="dimmed">
				{label}
			</Text>
			<Text size="sm" ff={mono ? "monospace" : undefined}>
				{value}
			</Text>
		</Stack>
	);
}
